import type { WebsiteThemeV0 } from '../website/v0';
import type { WebsiteThemeLegacy } from './entity';
import { migrateWebsiteThemeButtonLegacy } from './button';
import { migrateWebsiteThemeFontLegacy, normalizeLegacyFontDevices } from './font-migration';

/**
 * Legacy → v0. Colours come across unchanged; fonts are rebuilt and buttons lose two dead bags.
 *
 * **Fonts** are the part v0 reshapes most, because the legacy shape could not be typed: values
 * arrived as `'16px'`, `16` or `null` for the same field, and the breakpoint-first bags meant every
 * reader carried its own `tabletStyles ?? mobileStyles ?? styles` fallback.
 *
 * **Buttons** drop `styles` and `data`, both superseded by `base`. Every reader that still wanted
 * them was in the `simple` variant, which lives in `@libs/themes` and is not versioned.
 *
 * **`root`** is recognised and not carried: it belongs to that same mini-landing path.
 *
 * What is left alone is left alone deliberately. The legacy blob was never validated, so v0 is the
 * shape genuinely out there wherever that shape could be typed — rewriting more of it would mean
 * migrating every page in the wild on read, with no way to tell a real failure from a shape nobody
 * had seen.
 */
export function migrateWebsiteThemeLegacyToV0(legacy: WebsiteThemeLegacy): WebsiteThemeV0 {
    return {
        entityType: 'theme',
        entitySubType: 'website',
        version: 0,
        props: {
            ...(legacy.colors ? { colors: legacy.colors } : {}),
            ...(legacy.fonts ? { fonts: migrateWebsiteThemeFontsLegacy(legacy.fonts) } : {}),
            ...(legacy.buttons
                ? {
                      buttons: {
                          ...legacy.buttons,
                          selected: legacy.buttons.selected.map(migrateWebsiteThemeButtonLegacy),
                      },
                  }
                : {}),
        },
    };
}

/** Legacy `isSynchronized` is v0's `syncWithDesktop` — the name blocks and columns use for the same switch. */
function migrateWebsiteThemeFontsLegacy({
    isSynchronized,
    ...fonts
}: NonNullable<WebsiteThemeLegacy['fonts']>): NonNullable<WebsiteThemeV0['props']['fonts']> {
    return {
        ...fonts,
        selected: fonts.selected.map((font) =>
            migrateWebsiteThemeFontLegacy(normalizeLegacyFontDevices(font, isSynchronized ?? true))
        ),
        ...(isSynchronized === undefined ? {} : { syncWithDesktop: isSynchronized }),
    };
}
